import { useEffect, useState, type RefObject } from "react";

/**
 * Header layouts are chosen from the width of the rendered header, rather
 * than the browser viewport. That keeps the controls stable when a sidebar,
 * drawer, or split-view changes the conversation's available space.
 */
export type ConversationHeaderLayout = "full" | "compact" | "icon" | "overflow";

export const CONVERSATION_HEADER_LAYOUT_WIDTHS = {
  /** Below this width, keep header controls in the overflow menu. */
  overflow: 560,
  /** Below this width, task status is represented by its colored dot. */
  icon: 640,
  /** Below this width, labels use their compact/truncated presentation. */
  compact: 820,
} as const;

export function getConversationHeaderLayout(
  width: number | null | undefined,
): ConversationHeaderLayout {
  if (width == null || !Number.isFinite(width)) return "full";
  if (width < CONVERSATION_HEADER_LAYOUT_WIDTHS.overflow) return "overflow";
  if (width < CONVERSATION_HEADER_LAYOUT_WIDTHS.icon) return "icon";
  if (width < CONVERSATION_HEADER_LAYOUT_WIDTHS.compact) return "compact";
  return "full";
}

/** Observes the actual rendered conversation-header width. */
export function useConversationHeaderLayout(
  headerRef: RefObject<HTMLElement | null>,
): ConversationHeaderLayout {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const updateWidth = (nextWidth: number) => {
      const roundedWidth = Math.round(nextWidth);
      setWidth((currentWidth) => (
        currentWidth === roundedWidth ? currentWidth : roundedWidth
      ));
    };

    updateWidth(header.getBoundingClientRect().width);

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) updateWidth(entry.contentRect.width);
      });
      observer.observe(header);
      return () => observer.disconnect();
    }

    const handleWindowResize = () => updateWidth(header.getBoundingClientRect().width);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [headerRef]);

  return getConversationHeaderLayout(width);
}
